import { useMemo } from "react";
import {
	velavuFloorGeometryToFilledMapGeoJSON,
	velavuFloorGeometryToMapGeoJSON,
} from "../../state/floor/floor-wall-helper";
import { VelavuFloor, VelavuSite } from "velavu-js-api";
import { getFloorTarget } from "../../helper/floor-helper";
import {
	MapboxOverlayBox,
	MapHelperInstance,
	useStyleDependantMapEffect,
} from "../../helper/map-helper";
import { imageSourceBlank } from "./mapbox-injectable";
import whitelabel from "../../whitelabel";
import { FeatureCollection, MultiPolygon, Polygon } from "geojson";

const mapboxIDSiteFloorplanPrefix: string = "site-floorplan-";
const mapboxIDSiteFloorplanImagePrefix: string = "site-floorplan-image-";

export interface MapboxInjectableSiteFloorplansProps {
	sites?: VelavuSite[] | undefined;
	floorOverride?: { [key: string]: string | undefined };
	highlightedSiteIDs?: string[];
	belowLayer?: string;
}

interface ResolvedSiteFloorplan {
	floor: VelavuFloor;
	isFilled: boolean;
}

function drawFloorplans(
	box: MapboxOverlayBox,
	floorplans: ResolvedSiteFloorplan[],
	belowLayer?: string | undefined,
): string[] {
	const createdIDs: string[] = [];

	floorplans.forEach(({ floor, isFilled }) => {
		const mapboxID = mapboxIDSiteFloorplanPrefix + floor.id;
		createdIDs.push(mapboxID);

		//Generate filled or buffered geometry
		let geometry: FeatureCollection<Polygon | MultiPolygon>;
		if (isFilled) {
			geometry = velavuFloorGeometryToFilledMapGeoJSON(floor.geometry);
		} else {
			geometry = velavuFloorGeometryToMapGeoJSON(floor.geometry);
		}

		box.addSource(mapboxID, {
			type: "geojson",
			data: geometry,
		});

		box.addLayer(
			{
				id: mapboxID,
				source: mapboxID,
				type: "fill-extrusion",
				paint: {
					"fill-extrusion-color": whitelabel.palette.secondary700,
					"fill-extrusion-height": 3.5,

					"fill-extrusion-ambient-occlusion-intensity": 0.7,
					"fill-extrusion-opacity": 0.6,
				},
			},
			belowLayer,
		);
		belowLayer = mapboxID;
	});

	return createdIDs;
}

function drawFloorplanImages(
	box: MapboxOverlayBox,
	floorplans: ResolvedSiteFloorplan[],
	belowLayer?: string | undefined,
): string[] {
	const createdIDs: string[] = [];

	floorplans.forEach(({ floor, isFilled }) => {
		//Don't render floorplan images on filled sites
		if (isFilled) {
			return;
		}

		const mapboxID = mapboxIDSiteFloorplanImagePrefix + floor.id;
		createdIDs.push(mapboxID);

		let floorImageSource = { ...imageSourceBlank };
		if (floor.img !== undefined) {
			floorImageSource = {
				url: floor.img.url,
				coordinates: floor.img.coordinates,
			};
		}

		box.addSource(mapboxID, {
			type: "image",
			...floorImageSource,
		});

		box.addLayer(
			{
				id: mapboxID,
				source: mapboxID,
				type: "raster",
				paint: {
					"raster-fade-duration": 0,
					"raster-opacity": floor.img?.opacity ?? 0,
				},
			},
			belowLayer,
		);

		belowLayer = mapboxID;
	});

	return createdIDs;
}

export default function useMapboxInjectableSiteFloorplans(
	mapInstance: MapHelperInstance,
	props: MapboxInjectableSiteFloorplansProps,
): string | undefined {
	const allSites = props.sites;
	const floorOverride = props.floorOverride;
	const highlightedSiteIDs = useMemo(() => {
		return new Set(props.highlightedSiteIDs ?? []);
	}, [props.highlightedSiteIDs]);

	//Resolve the floor to show for each site
	const resolvedFloorSites = useMemo(() => {
		if (allSites === undefined) {
			return [];
		}

		//Filter out sites with no floors
		return allSites
			.map((site): ResolvedSiteFloorplan | null => {
				//Ignore sites with no floors
				if (site.floors === undefined || site.floors.length === 0) {
					return null;
				}

				//Resolve the floor to use
				const resolvedFloor = getFloorTarget(
					site.floors!,
					floorOverride?.[site.id],
				)!;
				return {
					floor: resolvedFloor,
					isFilled: !highlightedSiteIDs.has(site.id),
				};
			})
			.filter((it) => it !== null);
	}, [allSites, floorOverride, highlightedSiteIDs]);

	//Hash resolvedFloorSites to skip re-rendering if nothing actually changed
	const resolvedFloorSitesHash = useMemo(() => {
		return JSON.stringify(resolvedFloorSites);
	}, [resolvedFloorSites]);

	useStyleDependantMapEffect(
		mapInstance,
		(_, box) => {
			if (resolvedFloorSites.length === 0) {
				return;
			}

			const floorplanLayerIDs = drawFloorplans(
				box,
				resolvedFloorSites,
				props.belowLayer,
			);

			drawFloorplanImages(
				box,
				resolvedFloorSites,
				floorplanLayerIDs[floorplanLayerIDs.length - 1],
			);
		},
		[resolvedFloorSitesHash, props.belowLayer],
	);

	//Return the layer ID of the last rendered floorplan image layer
	for (const floorplan of resolvedFloorSites.toReversed()) {
		if (!floorplan.isFilled) {
			return mapboxIDSiteFloorplanImagePrefix + floorplan.floor.id;
		}
	}

	//Return the layer ID of the last rendered floorplan layer
	if (resolvedFloorSites.length !== 0) {
		return (
			mapboxIDSiteFloorplanPrefix +
			resolvedFloorSites[resolvedFloorSites.length - 1].floor.id
		);
	}

	//No data
	return undefined;
}
